import React, { useState, useRef, useEffect } from 'react';
import {
  Typography, Box, Card, Button, Switch,
  FormControlLabel, Tabs, Tab, Divider, 
  List, ListItem, ListItemText, Alert, Snackbar, Avatar,
  InputLabel, Skeleton, LinearProgress, CircularProgress,
  TextField, Grid, Paper, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl
} from '@mui/material';
import { useSettings } from '../../contexts/SettingsContext';
import ProductKeyFromFacturation from '../../components/setup/ProductKeyFromFacturation';
import {
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Key as KeyIcon,
  Key as LicenseIcon,
  VpnKey as VpnKeyIcon,
  CloudDone as CloudDoneIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Backup as BackupIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  AccountBalance as BankIcon,
  Receipt as InvoiceIcon,
  ColorLens as ColorLensIcon
} from '@mui/icons-material';

const ParametresEtendu = () => {
  const { settings, setSetting, uploadLogo, removeLogo, loading } = useSettings();
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false); // 🔑 Modal pour la configuration FACTURATION
  const [editMode, setEditMode] = useState({
    agency: false,
    licensing: false,
    banking: false
  });

  // États pour la nouvelle fonctionnalité de licensing
  const [licenseData, setLicenseData] = useState({
    productKey: settings.productKey || '',
    licenseStatus: settings.licenseStatus || 'inactive',
    licenseExpiry: settings.licenseExpiry || '',
    maxUsers: settings.maxUsers || 1,
    features: settings.features || []
  });

  // États pour les comptes bancaires
  const [bankAccounts, setBankAccounts] = useState([
    { type: 'CIB', account: '', name: settings.bankAccount1 || '' },
    { type: 'BADR', account: '', name: settings.bankAccount2 || '' },
    { type: 'BNA', account: '', name: settings.bankAccount3 || '' },
    { type: 'CCP', account: '', name: settings.bankAccount4 || '' }
  ]);

  // États pour les paramètres de design
  const [designSettings, setDesignSettings] = useState({
    primaryColor: settings.primaryColor || '#2563eb',
    secondaryColor: settings.secondaryColor || '#9333ea',
    logoPosition: settings.logoPosition || 'left',
    theme: settings.theme || 'light'
  });

  // États pour la sauvegarde
  const [backupSettings, setBackupSettings] = useState({
    autoBackup: settings.autoBackup || false,
    backupFrequency: settings.backupFrequency || 'daily',
    cloudSync: settings.cloudSync || false,
    lastBackup: settings.lastBackup || null
  });

  // Define SkeletonInput component for progressive loading
  const SkeletonInput = ({ height = 40 }) => (
    <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 2, width: '100%', mb: 2 }} />
  );
  
  // Create refs for form inputs
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const websiteRef = useRef(null);
  const licenseRef = useRef(null);
  const addressRef = useRef(null);
  const fileInputRef = useRef(null);
  const productKeyRef = useRef(null);
  
  // Initialize refs with current settings on component mount
  useEffect(() => {
    if (nameRef.current) nameRef.current.value = settings.agencyName || '';
    if (emailRef.current) emailRef.current.value = settings.agencyEmail || '';
    if (phoneRef.current) phoneRef.current.value = settings.agencyPhone || '';
    if (websiteRef.current) websiteRef.current.value = settings.agencyWebsite || '';
    if (licenseRef.current) licenseRef.current.value = settings.businessLicense || '';
    if (addressRef.current) addressRef.current.value = settings.agencyAddress || '';
    if (productKeyRef.current) productKeyRef.current.value = licenseData.productKey || '';
  }, [settings, licenseData]);

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setLogoUploading(true);
    
    try {
      await uploadLogo(file);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleLogoRemove = () => {
    removeLogo();
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Toggle edit mode
  const toggleEditMode = (section) => {
    setEditMode(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      
      // If we're exiting edit mode, save changes
      if (prev[section] && !newState[section]) {
        if (section === 'agency') {
          // Get values from refs
          setSetting('agencyName', nameRef.current?.value || '');
          setSetting('agencyEmail', emailRef.current?.value || '');
          setSetting('agencyPhone', phoneRef.current?.value || '');
          setSetting('agencyAddress', addressRef.current?.value || '');
          setSetting('agencyWebsite', websiteRef.current?.value || '');
          setSetting('businessLicense', licenseRef.current?.value || '');
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
        if (section === 'licensing') {
          // Save licensing data
          const newProductKey = productKeyRef.current?.value || '';
          setSetting('productKey', newProductKey);
          setLicenseData(prev => ({ ...prev, productKey: newProductKey }));
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      }
      
      return newState;
    });
  };

  // 🔑 REDIRECT TO FACTURATION-STYLE CONFIGURATION
  const openLicenseConfiguration = () => {
    // Ouvrir la vraie page de configuration comme dans FACTURATION
    console.log('🔑 TravelIT: Opening FACTURATION-style license configuration...');
    
    // Navigate to a dedicated licensing configuration page
    setShowLicenseModal(true);
  };

  // 🔑 HANDLE LICENSE VALIDATION FROM FACTURATION COMPONENT
  const handleLicenseValidated = () => {
    console.log('🎉 TravelIT: License validated successfully in FACTURATION-style component');
    
    // Update license data from localStorage (set by ProductKeyFromFacturation)
    const storedLicense = localStorage.getItem('travelit_license');
    if (storedLicense) {
      try {
        const licenseInfo = JSON.parse(storedLicense);
        setLicenseData(prev => ({
          ...prev,
          licenseStatus: 'active',
          productKey: licenseInfo.key,
          licenseExpiry: licenseInfo.activatedAt ? 
            new Date(new Date(licenseInfo.activatedAt).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString() : 
            '',
          maxUsers: 25, // TravelIT specific
          features: ['travel_booking', 'client_management', 'reports', 'api_access']
        }));

        // Update settings
        setSetting('licenseStatus', 'active');
        setSetting('productKey', licenseInfo.key);
      } catch (error) {
        console.error('Error parsing license data:', error);
      }
    }
    
    // Close modal and show success
    setShowLicenseModal(false);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // 🔑 CLOSE LICENSE MODAL
  const closeLicenseModal = () => {
    setShowLicenseModal(false);
  };

  // Handle backup creation
  const handleBackupCreation = async () => {
    try {
      // Simulate backup creation
      const backupData = {
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        data: {
          settings,
          clients: [], // Would be populated from database
          reservations: [], // Would be populated from database
          packages: [] // Would be populated from database
        }
      };

      const dataStr = JSON.stringify(backupData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `travelit-backup-${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();

      // Update last backup timestamp
      setSetting('lastBackup', new Date().toISOString());
      setBackupSettings(prev => ({ ...prev, lastBackup: new Date().toISOString() }));
      
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Backup creation failed:', error);
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
  
  // Custom Input component using native HTML elements
  const CustomInput = ({ label, defaultValue, inputRef, disabled, multiline = false, type = "text" }) => (
    <Box sx={{ mb: 2 }}>
      <InputLabel 
        sx={{ 
          mb: 1, 
          fontWeight: 500, 
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.875rem'
        }}
      >
        {label}
      </InputLabel>
      {multiline ? (
        <textarea
          ref={inputRef}
          defaultValue={defaultValue}
          disabled={disabled}
          rows={3}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            fontFamily: 'inherit',
            fontSize: '1rem',
            backgroundColor: disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            resize: 'vertical',
            outline: 'none'
          }}
        />
      ) : (
        <input
          ref={inputRef}
          defaultValue={defaultValue}
          disabled={disabled}
          type={type}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            fontFamily: 'inherit',
            fontSize: '1rem',
            backgroundColor: disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            outline: 'none'
          }}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
            Configuration Avancée
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Configurez tous les paramètres de votre agence et système de licensing
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            background: 'linear-gradient(to right, #2563eb, #9333ea)',
            color: '#fff',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(to right, #1e40af, #7e22ce)',
            }
          }}
        >
          <SaveIcon sx={{ mr: 1 }} />
          Enregistrer Tout
        </Button>
      </Box>

      {/* Configuration tabs */}
      <Card sx={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden'
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            p: 1,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '16px',
              mx: 0.5,
              minHeight: 64,
              '&.Mui-selected': {
                color: '#fff',
                background: 'linear-gradient(to right, #2563eb, #9333ea)',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            }
          }}
          variant="fullWidth"
        >
          <Tab icon={<BusinessIcon />} label="Agence" />
          <Tab icon={<KeyIcon />} label="Licensing" />
          <Tab icon={<PaletteIcon />} label="Design" />
          <Tab icon={<InvoiceIcon />} label="Facturation" />
          <Tab icon={<BankIcon />} label="Comptes" />
          <Tab icon={<BackupIcon />} label="Sauvegarde" />
          <Tab icon={<SecurityIcon />} label="Profil" />
        </Tabs>

        {/* Agency Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                Informations de l'Agence
              </Typography>
              <Button
                variant={editMode.agency ? "contained" : "outlined"}
                onClick={() => toggleEditMode('agency')}
                startIcon={editMode.agency ? <SaveIcon /> : null}
                sx={{ 
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  background: editMode.agency ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                  border: editMode.agency ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  '&:hover': {
                    background: editMode.agency ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {editMode.agency ? 'Enregistrer' : 'Modifier'}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Logo section */}
              <Box sx={{ width: { xs: '100%', md: '200px' }, textAlign: 'center' }}>
                {loading ? (
                  <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
                ) : settings.agencyLogo ? (
                  <Avatar
                    src={settings.agencyLogo}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      border: '3px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                      fontSize: '2rem',
                      fontWeight: 700,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    {settings.agencyName ? settings.agencyName.split(' ').map(word => word[0]).join('').slice(0, 3) : 'TRA'}
                  </Avatar>
                )}
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={triggerFileInput}
                    disabled={logoUploading}
                    size="small"
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.agencyLogo ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(to right, #2563eb, #9333ea)',
                      color: '#fff',
                      border: settings.agencyLogo ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                      '&:hover': {
                        background: settings.agencyLogo ? 'rgba(255, 255, 255, 0.2)' : 'linear-gradient(to right, #1e40af, #7e22ce)',
                      }
                    }}
                  >
                    {logoUploading ? 'Chargement...' : settings.agencyLogo ? 'Changer' : 'Logo'}
                  </Button>

                  {settings.agencyLogo && (
                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={handleLogoRemove}
                      size="small"
                      sx={{ 
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        color: 'rgba(255, 100, 100, 0.8)',
                        '&:hover': {
                          background: 'rgba(255, 0, 0, 0.1)',
                          border: '1px solid rgba(255, 0, 0, 0.5)',
                        }
                      }}
                    >
                      Supprimer
                    </Button>
                  )}
                </Box>
              </Box>
              
              {/* Agency details form */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                  gap: 2
                }}>
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
                    <CustomInput
                      label="Nom de l'agence"
                      defaultValue={settings.agencyName}
                      inputRef={nameRef}
                      disabled={!editMode.agency}
                    />
                  </Box>
                  
                  <CustomInput
                    label="Email"
                    defaultValue={settings.agencyEmail}
                    inputRef={emailRef}
                    disabled={!editMode.agency}
                    type="email"
                  />
                  
                  <CustomInput
                    label="Téléphone"
                    defaultValue={settings.agencyPhone}
                    inputRef={phoneRef}
                    disabled={!editMode.agency}
                  />
                  
                  <CustomInput
                    label="Site Web"
                    defaultValue={settings.agencyWebsite}
                    inputRef={websiteRef}
                    disabled={!editMode.agency}
                  />
                  
                  <CustomInput
                    label="Numéro de Licence"
                    defaultValue={settings.businessLicense}
                    inputRef={licenseRef}
                    disabled={!editMode.agency}
                  />
                  
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
                    <CustomInput
                      label="Adresse"
                      defaultValue={settings.agencyAddress}
                      inputRef={addressRef}
                      disabled={!editMode.agency}
                      multiline
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Licensing Tab - NEW FEATURE */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 3 }}>
            {/* 🔑 FACTURATION-STYLE LICENSE CONFIGURATION */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <SecurityIcon sx={{ color: '#3b82f6', fontSize: '4rem', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                  Configuration Produit TravelIT
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
                  Utilisez la même interface de configuration que INVOIT Facturation
                </Typography>

                {/* Current License Status (if exists) */}
                {licenseData.licenseStatus === 'active' && (
                  <Card sx={{ 
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '12px',
                    p: 2,
                    mb: 3,
                    maxWidth: 400,
                    mx: 'auto'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.5rem' }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                          Licence Active
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {licenseData.maxUsers} utilisateurs • {licenseData.features.length} fonctionnalités
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                )}

                {/* Main Configuration Button */}
                <Button
                  variant="contained"
                  onClick={openLicenseConfiguration}
                  startIcon={<KeyIcon />}
                  sx={{ 
                    borderRadius: '16px',
                    background: 'linear-gradient(to right, #2563eb, #9333ea)',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 8px 32px rgba(37, 99, 235, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(to right, #1e40af, #7e22ce)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(37, 99, 235, 0.4)',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {licenseData.licenseStatus === 'active' ? 'Modifier la Configuration' : 'Configurer TravelIT'}
                </Button>

                {/* Info Text */}
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 2, maxWidth: 400 }}>
                  Cette configuration utilise la même interface sécurisée que INVOIT Facturation avec le système de licensing centralisé.
                </Typography>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Design Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', mb: 3 }}>
              Paramètres de Design
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  p: 3
                }}>
                  <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                    Couleurs de Thème
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      Couleur Primaire
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <input
                        type="color"
                        value={designSettings.primaryColor}
                        onChange={(e) => setDesignSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                        style={{
                          width: '50px',
                          height: '40px',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      />
                      <Typography sx={{ color: '#fff' }}>{designSettings.primaryColor}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      Couleur Secondaire
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <input
                        type="color"
                        value={designSettings.secondaryColor}
                        onChange={(e) => setDesignSettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        style={{
                          width: '50px',
                          height: '40px',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      />
                      <Typography sx={{ color: '#fff' }}>{designSettings.secondaryColor}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  p: 3
                }}>
                  <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                    Configuration de l'Interface
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      Position du Logo
                    </Typography>
                    <Select
                      value={designSettings.logoPosition}
                      onChange={(e) => setDesignSettings(prev => ({ ...prev, logoPosition: e.target.value }))}
                      sx={{
                        color: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#fff'
                        }
                      }}
                    >
                      <MenuItem value="left">Gauche</MenuItem>
                      <MenuItem value="center">Centre</MenuItem>
                      <MenuItem value="right">Droite</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      Thème
                    </Typography>
                    <Select
                      value={designSettings.theme}
                      onChange={(e) => setDesignSettings(prev => ({ ...prev, theme: e.target.value }))}
                      sx={{
                        color: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#fff'
                        }
                      }}
                    >
                      <MenuItem value="light">Clair</MenuItem>
                      <MenuItem value="dark">Sombre</MenuItem>
                      <MenuItem value="auto">Automatique</MenuItem>
                    </Select>
                  </FormControl>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Invoicing Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
              Paramètres de Facturation
            </Typography>
            
            <List>
              <ListItem 
                disableRipple 
                disableTouchRipple
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '16px', 
                  mb: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                  }
                }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>Devise par défaut</Typography>}
                  secondary={<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Devise utilisée pour les prix</Typography>}
                />
                <Box>
                  <Button 
                    variant={settings.defaultCurrency === 'DZD' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'DZD')}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.defaultCurrency === 'DZD' ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.defaultCurrency === 'DZD' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.defaultCurrency === 'DZD' ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    DZD
                  </Button>
                  <Button 
                    variant={settings.defaultCurrency === 'EUR' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'EUR')}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.defaultCurrency === 'EUR' ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.defaultCurrency === 'EUR' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.defaultCurrency === 'EUR' ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    EUR
                  </Button>
                  <Button 
                    variant={settings.defaultCurrency === 'USD' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'USD')}
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.defaultCurrency === 'USD' ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.defaultCurrency === 'USD' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.defaultCurrency === 'USD' ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    USD
                  </Button>
                </Box>
              </ListItem>

              <ListItem 
                disableRipple 
                disableTouchRipple
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '16px', 
                  mb: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                  }
                }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>Taux de TVA</Typography>}
                  secondary={<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Pourcentage de TVA appliqué</Typography>}
                />
                <Box>
                  <Button 
                    variant={settings.taxRate === 19 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 19)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.taxRate === 19 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.taxRate === 19 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.taxRate === 19 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    19%
                  </Button>
                  <Button 
                    variant={settings.taxRate === 9 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 9)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.taxRate === 9 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.taxRate === 9 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.taxRate === 9 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    9%
                  </Button>
                  <Button 
                    variant={settings.taxRate === 0 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 0)}
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.taxRate === 0 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.taxRate === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.taxRate === 0 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    0%
                  </Button>
                </Box>
              </ListItem>

              <ListItem sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '16px', 
                mb: 2,
                p: 3,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'transparent !important'
                }
              }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>Commission par défaut</Typography>}
                  secondary={<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Pourcentage de commission sur les ventes</Typography>}
                />
                <Box>
                  <Button 
                    variant={settings.commissionRate === 10 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 10)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.commissionRate === 10 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.commissionRate === 10 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.commissionRate === 10 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    10%
                  </Button>
                  <Button 
                    variant={settings.commissionRate === 15 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 15)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.commissionRate === 15 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.commissionRate === 15 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.commissionRate === 15 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    15%
                  </Button>
                  <Button 
                    variant={settings.commissionRate === 20 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 20)}
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.commissionRate === 20 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.commissionRate === 20 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.commissionRate === 20 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    20%
                  </Button>
                </Box>
              </ListItem>
            </List>
          </Box>
        </TabPanel>

        {/* Banking Tab */}
        <TabPanel value={activeTab} index={4}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                Comptes Bancaires
              </Typography>
              <Button
                variant={editMode.banking ? "contained" : "outlined"}
                onClick={() => toggleEditMode('banking')}
                startIcon={editMode.banking ? <SaveIcon /> : null}
                sx={{ 
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  background: editMode.banking ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                  border: editMode.banking ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  '&:hover': {
                    background: editMode.banking ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                  }
                }}
              >
                {editMode.banking ? 'Enregistrer' : 'Modifier'}
              </Button>
            </Box>

            <Grid container spacing={2}>
              {bankAccounts.map((account, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card sx={{ 
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '16px',
                    p: 2
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                      {account.type}
                    </Typography>
                    <CustomInput
                      label="Nom de Compte"
                      defaultValue={account.name}
                      disabled={!editMode.banking}
                    />
                    <CustomInput
                      label="Numéro de Compte"
                      defaultValue={account.account}
                      disabled={!editMode.banking}
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Backup Tab */}
        <TabPanel value={activeTab} index={5}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
              Sauvegarde et Restauration
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  p: 3
                }}>
                  <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                    Sauvegarde Automatique
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={backupSettings.autoBackup}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
                      />
                    }
                    label="Activer la sauvegarde automatique"
                    sx={{ color: '#fff', mb: 2, display: 'block' }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      Fréquence
                    </Typography>
                    <Select
                      value={backupSettings.backupFrequency}
                      onChange={(e) => setBackupSettings(prev => ({ ...prev, backupFrequency: e.target.value }))}
                      disabled={!backupSettings.autoBackup}
                      sx={{
                        color: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#fff'
                        }
                      }}
                    >
                      <MenuItem value="daily">Quotidienne</MenuItem>
                      <MenuItem value="weekly">Hebdomadaire</MenuItem>
                      <MenuItem value="monthly">Mensuelle</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={backupSettings.cloudSync}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, cloudSync: e.target.checked }))}
                      />
                    }
                    label="Synchronisation cloud"
                    sx={{ color: '#fff' }}
                  />
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  p: 3
                }}>
                  <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                    Actions Manuelles
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                      Dernière sauvegarde: {backupSettings.lastBackup ? 
                        new Date(backupSettings.lastBackup).toLocaleString('fr-FR') : 'Jamais'}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleBackupCreation}
                      startIcon={<DownloadIcon />}
                      sx={{
                        mb: 2,
                        borderRadius: '12px',
                        background: 'linear-gradient(to right, #2563eb, #9333ea)',
                        '&:hover': {
                          background: 'linear-gradient(to right, #1e40af, #7e22ce)',
                        }
                      }}
                    >
                      Créer une Sauvegarde
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<UploadIcon />}
                      sx={{
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: '#fff',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      Restaurer une Sauvegarde
                    </Button>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Profile Tab - Same as before */}
        <TabPanel value={activeTab} index={6}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
              Profil Utilisateur
            </Typography>
            
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 4 },
              mb: { xs: 3, md: 4 }
            }}>
              {/* User Avatar Section */}
              <Box sx={{ 
                width: { xs: '100%', md: '250px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: { xs: 1, md: 0 }
              }}>
                <Avatar
                  src={null}
                  sx={{
                    width: { xs: 120, sm: 140, md: 150 },
                    height: { xs: 120, sm: 140, md: 150 },
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                    border: '3px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {'A'}
                </Avatar>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 0.5,
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    color: 'white'
                  }}
                >
                  {'Ahmed'}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.8125rem', md: '0.875rem' },
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}
                >
                  {'Administrateur'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        <Divider />
      </Card>

      {/* Success notification */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ borderRadius: '12px' }}>
          Configuration mise à jour avec succès !
        </Alert>
      </Snackbar>

      {/* 🔑 FACTURATION-STYLE LICENSE CONFIGURATION MODAL */}
      <Dialog
        open={showLicenseModal}
        onClose={closeLicenseModal}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 50%, #581c87 100%)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ProductKeyFromFacturation 
            onLicenseValidated={handleLicenseValidated}
            onBack={closeLicenseModal}
            onNext={handleLicenseValidated}
          />
        </DialogContent>
      </Dialog>

      {/* App info */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          TravelIT v{settings?.appVersion || '1.0.0'} — Configuration Étendue avec Licensing
        </Typography>
      </Box>
    </Box>
  );
};

export default ParametresEtendu;
